/**
 * Analytics: GA4 + Meta Pixel with event_id deduplication for CAPI
 * REACT_APP_GA4_ID        — Google Analytics 4 Measurement ID
 * REACT_APP_META_PIXEL_ID — Meta/Facebook Pixel ID
 */

const GA4_ID = process.env.REACT_APP_GA4_ID;
const META_PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID;
const API = process.env.REACT_APP_BACKEND_URL || "";

let ga4Loaded = false;
let metaLoaded = false;

/** Generate unique event ID for deduplication between Pixel + CAPI */
function genEventId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

// ─── GA4 Setup ───
export function initGA4() {
  if (!GA4_ID || ga4Loaded) return;
  ga4Loaded = true;
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`;
  document.head.appendChild(script);
  window.dataLayer = window.dataLayer || [];
  window.gtag = function () { window.dataLayer.push(arguments); };
  window.gtag("js", new Date());
  window.gtag("config", GA4_ID, { send_page_view: false });
}

// ─── Meta Pixel Setup ───
export function initMetaPixel() {
  if (!META_PIXEL_ID || metaLoaded) return;
  metaLoaded = true;
  /* eslint-disable */
  !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
  n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;
  n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
  t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,
  document,'script','https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable */
  window.fbq("init", META_PIXEL_ID);
}

// ─── Initialize All ───
export function initAnalytics() {
  initGA4();
  initMetaPixel();
}

/** Send server-side CAPI event (fire-and-forget) */
function sendCAPI(eventName, eventId, params = {}) {
  if (!META_PIXEL_ID) return;
  fetch(`${API}/api/meta/capi`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      event_name: eventName,
      event_id: eventId,
      event_source_url: window.location.href,
      user_agent: navigator.userAgent,
      ...params,
    }),
  }).catch(() => {}); // fire-and-forget
}

// ─── PageView ───
export function trackPageView(path, title) {
  const eventId = genEventId();
  if (GA4_ID && window.gtag) {
    window.gtag("event", "page_view", { page_path: path, page_title: title });
  }
  if (META_PIXEL_ID && window.fbq) {
    window.fbq("track", "PageView", {}, { eventID: eventId });
  }
  sendCAPI("PageView", eventId);
}

// ─── View Product (ViewContent) ───
export function trackViewProduct(product) {
  if (!product) return;
  const price = product.variants?.[0]?.price || product.price || 0;
  const eventId = genEventId();

  if (GA4_ID && window.gtag) {
    window.gtag("event", "view_item", {
      currency: "USD", value: price,
      items: [{ item_id: product.id, item_name: product.name, item_brand: product.brand, price }],
    });
  }
  if (META_PIXEL_ID && window.fbq) {
    window.fbq("track", "ViewContent", {
      content_name: product.name,
      content_ids: [product.id],
      content_type: "product",
      value: price,
      currency: "USD",
    }, { eventID: eventId });
  }
  sendCAPI("ViewContent", eventId, {
    content_name: product.name,
    content_ids: [product.id],
    value: price,
    currency: "USD",
  });
}

// ─── Add to Cart ───
export function trackAddToCart(product, variant, quantity = 1) {
  const price = variant?.price || 0;
  const eventId = genEventId();

  if (GA4_ID && window.gtag) {
    window.gtag("event", "add_to_cart", {
      currency: "USD", value: price * quantity,
      items: [{ item_id: product.id, item_name: product.name, item_brand: product.brand, price, quantity }],
    });
  }
  if (META_PIXEL_ID && window.fbq) {
    window.fbq("track", "AddToCart", {
      content_name: product.name,
      content_ids: [product.id],
      content_type: "product",
      value: price * quantity,
      currency: "USD",
      contents: [{ id: product.id, quantity }],
    }, { eventID: eventId });
  }
  sendCAPI("AddToCart", eventId, {
    content_name: product.name,
    content_ids: [product.id],
    value: price * quantity,
    currency: "USD",
    contents: [{ id: product.id, quantity }],
  });
}

// ─── Begin Checkout (InitiateCheckout) ───
export function trackBeginCheckout(items, subtotal) {
  const eventId = genEventId();
  const contentIds = items.map((i) => i.product.id);
  const numItems = items.reduce((s, i) => s + i.quantity, 0);

  if (GA4_ID && window.gtag) {
    window.gtag("event", "begin_checkout", {
      currency: "USD", value: subtotal,
      items: items.map((i) => ({
        item_id: i.product.id, item_name: i.product.name,
        item_brand: i.product.brand, price: i.variant.price, quantity: i.quantity,
      })),
    });
  }
  if (META_PIXEL_ID && window.fbq) {
    window.fbq("track", "InitiateCheckout", {
      content_ids: contentIds,
      content_type: "product",
      num_items: numItems,
      value: subtotal,
      currency: "USD",
      contents: items.map((i) => ({ id: i.product.id, quantity: i.quantity })),
    }, { eventID: eventId });
  }
  sendCAPI("InitiateCheckout", eventId, {
    content_ids: contentIds,
    value: subtotal,
    currency: "USD",
    num_items: numItems,
    contents: items.map((i) => ({ id: i.product.id, quantity: i.quantity })),
  });

  // Store checkout data for potential Purchase event
  try {
    localStorage.setItem("doria_checkout_data", JSON.stringify({
      items: items.map((i) => ({ id: i.product.id, name: i.product.name, quantity: i.quantity, price: i.variant.price })),
      subtotal,
      eventId,
      timestamp: Date.now(),
    }));
  } catch {}
}

// ─── Search ───
export function trackSearch(query) {
  const eventId = genEventId();
  if (GA4_ID && window.gtag) {
    window.gtag("event", "search", { search_term: query });
  }
  if (META_PIXEL_ID && window.fbq) {
    window.fbq("track", "Search", { search_string: query }, { eventID: eventId });
  }
  sendCAPI("Search", eventId, { search_string: query });
}

// ─── Newsletter Signup ───
export function trackNewsletterSignup() {
  if (GA4_ID && window.gtag) {
    window.gtag("event", "sign_up", { method: "newsletter" });
  }
  if (META_PIXEL_ID && window.fbq) {
    window.fbq("track", "Lead", { content_name: "Newsletter" });
  }
}

// ─── Quiz Complete ───
export function trackQuizComplete() {
  if (GA4_ID && window.gtag) {
    window.gtag("event", "quiz_complete", { event_category: "engagement" });
  }
  if (META_PIXEL_ID && window.fbq) {
    window.fbq("trackCustom", "QuizComplete");
  }
}
